import clsx from 'clsx';
import { EQuestionType } from '@hubertai/screening-question-types';
import { Info } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ECreateQuestionMode, useQuestionEditorContext } from '../../context/create-question.context';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertDialog from 'components/HubertUI/HubertDialog';
import HubertLabel from 'components/HubertUI/HubertLabel';
import HubertTooltip from 'components/HubertUI/HubertTooltip';
import { useInTemplateEditAndUneditable } from 'hooks/useInTemplateEditAndUneditable';
import { useUser } from 'hooks/useUser';
import { isHubertAdminUser } from 'types/user.types';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { addOption, clearOptions } from 'rtk/Question.Editor.Slice';
import { setShowDisabledOptions, setShowOptionReferences } from 'rtk/layout/LayoutControlSlice';
import {
  selectOptionReferencesVisible,
  selectQuestionHasOptionReferences,
  selectQuestionIsReadonly,
  selectQuestionLanguage,
  selectQuestionMode,
  selectQuestionOptions,
  selectShowDisabledOptions,
} from 'rtk/questionEditorSelector';
import HubertSwitch from 'components/HubertUI/HubertSwitch';
import MutedText from 'components/common/MutedText';

const CreateOptionHeader = ({ ...props }) => {
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const isReadOnly = useAppSelector((state) => selectQuestionIsReadonly(state, questionId));
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const inTemplateEditAndUneditable = useInTemplateEditAndUneditable(questionId, mode);
  const isTemplateEdit = mode === ECreateQuestionMode.InTemplateEdit;
  const showDisabled = useAppSelector((state) => selectShowDisabledOptions(state));
  const referencesVisible = useAppSelector((state) => selectOptionReferencesVisible(state, questionId));
  const hasExistingReferences = useAppSelector((state) => selectQuestionHasOptionReferences(state, questionId));
  const type = useAppSelector((state) => state.questionEditor.byId[questionId].type);
  const options = useAppSelector((state) => selectQuestionOptions(state, questionId));
  const [isClearDialogOpen, setIsClearDialogOpen] = useState(false);
  const { t } = useTranslation('Questionsv2');
  const user = useUser();
  const [portalContainer] = useState(() => document.getElementById('main-content') as HTMLElement);

  return (
    <div {...props} className={clsx('flex flex-row items-center justify-between gap-2', 'w-full', 'px-2')}>
      <div className="flex flex-row items-center justify-start gap-2">
        <HubertTooltip>
          <HubertTooltip.Trigger asChild>
            <Info className="ml-1 text-gray-400" />
          </HubertTooltip.Trigger>
          <HubertTooltip.Content portalContent>{t('selects.editTooltip')}</HubertTooltip.Content>
        </HubertTooltip>
        <HubertLabel htmlFor="create-question-text">{t('selects.label')}</HubertLabel>
      </div>

      <div className="flex flex-row gap-4">
        {isTemplateEdit && (
          <div className="flex flex-row gap-2">
            <MutedText className="mb-1 flex items-center gap-1">{t('selects.showDisabled')}</MutedText>
            <HubertSwitch
              checked={showDisabled}
              onCheckedChange={(value) => {
                dispatch(setShowDisabledOptions(value));
              }}
            />
          </div>
        )}
        {type === EQuestionType.SingleSelect && isHubertAdminUser(user) && (
          // A disabled switch swallows hover, so the whole row is the trigger. Only explains when locked.
          <HubertTooltip open={hasExistingReferences ? undefined : false}>
            <HubertTooltip.Trigger asChild>
              <div className="flex flex-row gap-2">
                <MutedText className="mb-1 flex items-center gap-1">{t('selects.showReferences')}</MutedText>
                <HubertSwitch
                  checked={referencesVisible}
                  disabled={hasExistingReferences}
                  onCheckedChange={(value) => {
                    dispatch(setShowOptionReferences(value));
                  }}
                />
              </div>
            </HubertTooltip.Trigger>
            <HubertTooltip.Content portalContent>{t('selects.showReferencesLocked')}</HubertTooltip.Content>
          </HubertTooltip>
        )}
        {type === EQuestionType.SingleSelect && options.length > 0 ? (
          <HubertDialog open={isClearDialogOpen} overlay={true} modal={true} onOpenChange={setIsClearDialogOpen}>
            <HubertDialog.Trigger>
              <HubertButton
                variant={'destructive'}
                size={'xs'}
                className={clsx(isReadOnly ? 'hidden' : '')}
                disabled={inTemplateEditAndUneditable}
              >
                {t('selects.clearOptions')}
              </HubertButton>
            </HubertDialog.Trigger>
            <HubertDialog.Portal>
              <HubertDialog.Content
                position="center"
                portalContent={true}
                portalElement={portalContainer}
                className="w-[min(90vw,420px)]!"
              >
                <HubertDialog.Title className="mb-4">{t('selects.clearOptionsDialog.title')}</HubertDialog.Title>
                <MutedText>{t('selects.clearOptionsDialog.description', { count: options.length })}</MutedText>
                <div className={clsx('flex flex-row justify-between gap-4', 'mt-4')}>
                  <HubertButton variant={'secondary'} size={'sm'} onClick={() => setIsClearDialogOpen(false)}>
                    {t('selects.clearOptionsDialog.cancelButton')}
                  </HubertButton>
                  <HubertButton
                    variant={'destructive'}
                    size={'sm'}
                    onClick={() => {
                      dispatch(clearOptions({ questionId }));
                      setIsClearDialogOpen(false);
                    }}
                  >
                    {t('selects.clearOptionsDialog.confirmButton')}
                  </HubertButton>
                </div>
              </HubertDialog.Content>
            </HubertDialog.Portal>
          </HubertDialog>
        ) : null}
        <HubertButton
          size={'xs'}
          onClick={() => {
            dispatch(addOption({ questionId, language: questionLanguage }));
          }}
          className={clsx(isReadOnly ? 'hidden' : '')}
          disabled={inTemplateEditAndUneditable}
        >
          {t('selects.addOption')}
        </HubertButton>
      </div>
    </div>
  );
};

export default CreateOptionHeader;
